import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAuthData, getSessinIdFromLS } from "../../../services/storage/LocalStorage";
import { ImagePath } from "../../../assets/ImagePath";
import { clearChatwidgetNotifications } from "../../../state/actions/chats";
import { setHideChatwidget } from "../../../state/actions/bot";
import { appType } from "../../../utils/config/configHelpers";
import { getPageName } from "../../../utils/general";
import {
  fullBotScriptReset, loadBotScriptStyles, removeBotScript,
  scriptSrc, sendParamsToEmbeddedChatbot
} from "../../../utils/ChatbotScriptHelpers";

import FrontmGifLogoLoader from "../../ui/Loader/frontmGifLogoLoader";
import config from "../../../utils/config/config";
import { SOCKET } from "../../../services/socket/initWebSocket";


const FMChatWidget = ({connectionId,refreshToken}) => {
  const selectedConversation = useSelector((state) => state?.chats?.selectedConversation);
  const selectedBotDetails = useSelector((state) => state.domain?.selectedBotDetails);
  const chatwidgetNotifications = useSelector((state) => state?.chats?.chatwidgetNotifications);
  const isOpenChatWidget = useSelector((state) => state.modal.isOpenChatWidget);
  const profileImage = useSelector((state) => state.user?.profileImage || null);

  const [loader, setLoader] = useState(true);
  const dispatch = useDispatch();
  const userDetail = getAuthData();
  const sessionId = getSessinIdFromLS();
  const userData = userDetail?.user || {};
  const currentBotIdRef = useRef(null);
  const currentConversationRef = useRef(null);

  const pathname = getPageName();
  const styleId = "chatbot-as-widget-styles";
  const stylePath = "/scripts/chatwidget/static/css/main.5802ba76.css";

  useEffect(() => {
    if (loader) {
      const minDelay = setTimeout(() => setLoader(false), 8000);
      return () => clearTimeout(minDelay);
    }
  }, [loader]);


  useEffect(() => {
    if ((chatwidgetNotifications && chatwidgetNotifications.length > 0) && isOpenChatWidget) {
      dispatch(clearChatwidgetNotifications());
    }
  }, [chatwidgetNotifications, isOpenChatWidget]);


  useEffect(() => {
    window.loadingFalseIntoChatBot = () => {
      setTimeout(() => setLoader(false), 100);
    };

    return () => {
      delete window.loadingFalseIntoChatBot;
      currentBotIdRef.current = null;
      currentConversationRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (pathname === "Home") {
      window.CurrentEnvironement = process.env.REACT_APP_ENV || 'development';
      const botId = selectedConversation?.botId || selectedConversation?.bot?.botId;
      const convId = selectedConversation?.conversationId;

      const isValid = botId && convId;
      const isChanged =
        currentBotIdRef.current !== botId ||
        currentConversationRef.current !== convId;    

      if (isValid && isChanged && (pathname === "Home") && 
      (selectedBotDetails && !selectedBotDetails?.conversational)) {
        performCleanupAndLoad();
        currentBotIdRef.current = botId;
        currentConversationRef.current = convId;
      }
    }
    else {
      currentBotIdRef.current = null;
      currentConversationRef.current = null;
    }
  }, [selectedConversation]);


  const performCleanupAndLoad = async () => {
    const response = await fullBotScriptReset();
    if (response === true) {
      setTimeout(() => {
        loadBotScriptStyles(styleId, stylePath);
        loadBotScript();
      }, 300);
    }
  };


  const loadBotScript = () => {
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

    if (existingScript) {
      // console.log('Script already loaded for chatwidget, removing and reloading...');
      removeBotScript();
    }

    setTimeout(() => {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = () => {
        // console.log("Chatwidget Script loaded successfully.");
        setTimeout(() => {
          const params = {
            isChatWidget: true,
            userEmail: userData?.emailAddress || "",
            sessionId: sessionId || userDetail?.auth?.sessionId,
            appType: appType,
            conversationId: selectedConversation?.conversationId || "",
            platform: "web",
            botId: selectedConversation?.bot?.botId || selectedConversation?.botId || selectedBotDetails?.botId,
            botName: selectedConversation?.bot?.name || selectedBotDetails?.botName || "",
            botLogoUrl: selectedConversation?.bot?.logoUrl || selectedBotDetails?.logoUrl || "",
            userId: userDetail?.user?.userId || "",
            connectionId: connectionId,
            refreshToken: refreshToken,
            userProfileImage: profileImage || "",
            userDetails: userData || "",
            ws: SOCKET.ws
          };
          sendParamsToEmbeddedChatbot(params);
        }, 200);
      };
      script.onerror = (e) => {
        console.error("Error loading the script: ", e);
        setLoader(false)
      };
      document.body.appendChild(script);
    }, 100);
  };


  const handleCloseClick = () => {
    dispatch(setHideChatwidget());
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          background: "#fff",
          borderRadius: "20px",
          marginTop: "50px",
        }}
        className="color-red bg-white flex flex-col overflow-hidden shadow-shd-on"
      >
        {/* Header with Close Icon */}
        <div className="flex justify-between items-center p-6 bg-[#15194B]">
          {/* Title */}
          <div className="flex items-center gap-3">
            <img
              src={selectedBotDetails?.logoUrl ? `${config.contentURL}${selectedBotDetails.logoUrl}` : ImagePath.EmmaLogo}
              role="button"
              className="w-[21px] h-[21px] object-cover rounded-7"
            />
            <h2 className="text-white text-size-16 font-bold">
              {selectedBotDetails?.botName || ""}
            </h2>
          </div>
          {/* Close Button */}
          <button
            onClick={handleCloseClick}
            className="text-white bg-[#fff] rounded-full p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="#15194B"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {loader && (
          <FrontmGifLogoLoader className="!absolute !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2 z-[999]" />
        )}
        {/* Chatbot Embed Container */}
        <div className="flex-1 bg-[#FFF]">
          <div id="chat-bot-project-root"></div>
        </div>
      </div>
    </>
  );
};

export default FMChatWidget;
